'use client'

import { useState, useEffect } from 'react'
import styles from './ColorPalette.module.css'

interface ColorVariable {
  name: string
  category: string
  defaultValue: string
}

const COLOR_VARIABLES: ColorVariable[] = [
  // Brand Colors
  { name: '--primary', category: 'Brand Colors', defaultValue: '13, 64, 86' },
  { name: '--primary2', category: 'Brand Colors', defaultValue: '0, 197, 154, 0.50' },
  { name: '--secondary', category: 'Brand Colors', defaultValue: '77, 44, 148' },
  { name: '--secondary2', category: 'Brand Colors', defaultValue: '138, 51, 222' },
  { name: '--secondary3', category: 'Brand Colors', defaultValue: '200, 112, 255' },
  { name: '--secondary4', category: 'Brand Colors', defaultValue: '73, 32, 116' },
  { name: '--suggested1', category: 'Brand Colors', defaultValue: '0, 196, 153' },
  { name: '--suggested2', category: 'Brand Colors', defaultValue: '0, 123, 167' },
  { name: '--white', category: 'Brand Colors', defaultValue: '255, 255, 255' },
  
  // Background Colors
  { name: '--background', category: 'Background Colors', defaultValue: '20, 17, 28' },
  { name: '--overlayBackground', category: 'Background Colors', defaultValue: '0, 0, 0, 0.5' },
  { name: '--surfaceOpaque', category: 'Background Colors', defaultValue: '23, 16, 41, 1' },
  { name: '--surface', category: 'Background Colors', defaultValue: '23, 16, 41, 0.90' },
  { name: '--submenu', category: 'Background Colors', defaultValue: '23, 16, 41, 0.85' },
  { name: '--cardContainer', category: 'Background Colors', defaultValue: '34, 28, 52, 0.95' },
  { name: '--destructiveBg', category: 'Background Colors', defaultValue: '255, 56, 60, 0.2' },
  
  // Text Colors
  { name: '--textLight', category: 'Text Colors', defaultValue: '255, 255, 255' },
  { name: '--textButton', category: 'Text Colors', defaultValue: '255, 255, 255' },
  { name: '--textInfo', category: 'Text Colors', defaultValue: '164, 167, 171' },
  { name: '--textHoverHighlight', category: 'Text Colors', defaultValue: '1, 246, 193' },
  { name: '--textHoverSecondary', category: 'Text Colors', defaultValue: '200, 112, 255' },
  { name: '--textDisabled', category: 'Text Colors', defaultValue: '111, 108, 115' },
  
  // Icon Buttons
  { name: '--iconLight', category: 'Icon Buttons', defaultValue: '255, 255, 255' },
  { name: '--iconHoverHighlight', category: 'Icon Buttons', defaultValue: '1, 246, 193' },
  { name: '--iconLightDisabled', category: 'Icon Buttons', defaultValue: '111, 108, 115' },
  { name: '--fieldHoverBg', category: 'Icon Buttons', defaultValue: '49, 26, 97, 0.90' },
  { name: '--disabledBtn', category: 'Icon Buttons', defaultValue: '49, 44, 61' },
  { name: '--tooltipBg', category: 'Icon Buttons', defaultValue: '78, 67, 100' },
  { name: '--iconEnableBtnDisabled', category: 'Icon Buttons', defaultValue: '111, 108, 115' },
  
  // Strokes
  { name: '--strokeDefault', category: 'Strokes', defaultValue: '152, 128, 246' },
  { name: '--strokeDivider', category: 'Strokes', defaultValue: '78, 67, 100' },
  { name: '--highlight', category: 'Strokes', defaultValue: '1, 246, 193' },
  { name: '--strokeHoverSecondary', category: 'Strokes', defaultValue: '200, 112, 255' },
  { name: '--suggestedSolidLineForHoverBtn', category: 'Strokes', defaultValue: '0, 123, 167' },
  { name: '--accentRed', category: 'Strokes', defaultValue: '255, 56, 60' },
  
  // Label Colors
  { name: '--labelNew', category: 'Label Colors', defaultValue: '255, 153, 52' },
  { name: '--labelForYou', category: 'Label Colors', defaultValue: '0, 217, 255' },
  { name: '--labelLicense', category: 'Label Colors', defaultValue: '255, 255, 0' },
  { name: '--requiresAccountLabel', category: 'Label Colors', defaultValue: '246, 113, 255' },
  { name: '--hoverLabel', category: 'Label Colors', defaultValue: '1, 246, 193' },
  { name: '--floatingDotsLabel', category: 'Label Colors', defaultValue: '255, 255, 255' },
  
  // Notification Colors
  { name: '--warningMessage', category: 'Notification Colors', defaultValue: '255, 158, 24' },
  { name: '--errorMessage', category: 'Notification Colors', defaultValue: '252, 61, 61' },
  { name: '--successMessage', category: 'Notification Colors', defaultValue: '18, 186, 55' },
  { name: '--infoMessage', category: 'Notification Colors', defaultValue: '225, 226, 228' },
  
  // Discount Colors
  { name: '--discount', category: 'Discount Colors', defaultValue: '18, 186, 55' },
  { name: '--discountHover', category: 'Discount Colors', defaultValue: '116, 225, 130' },
]

// Gradientes (no editables)
const GRADIENT_VARIABLES = [
  { name: '--backgroundSparkles', defaultValue: 'radial-gradient(50% 50% at 50% 50%, rgba(var(--secondary), 0.60) 0%, rgba(var(--background), 0.00) 100%)' },
  { name: '--primaryButton', defaultValue: 'radial-gradient(56.25% 56.25% at 50.72% 100%, rgba(var(--primary2)) 0%, rgba(var(--primary)) 100%)' },
  { name: '--primaryButtonHover', defaultValue: 'radial-gradient(56.25% 56.25% at 50.72% 100%, rgba(var(--primary)) 0%, rgba(var(--primary2)) 100%)' },
  { name: '--secondaryButton', defaultValue: 'radial-gradient(68.03% 68.03% at 50.72% 91.35%,  rgba(var(--secondary)) 0%,  rgba(var(--secondary4)) 100%)' },
  { name: '--secondaryButtonHover', defaultValue: 'radial-gradient(55.56% 55.56% at 49.64% 87.04%, rgba(var(--secondary4)) 0%, rgb(var(--secondary2)) 100%)' },
  { name: '--suggestedButton', defaultValue: 'radial-gradient(69.21% 69.21% at 50.72% 87.04%, rgba(var(--suggested2)) 13.94%, rgba(var(--suggested1)) 100%)' },
  { name: '--suggestedButtonHover', defaultValue: 'radial-gradient(56.25% 56.25% at 50.72% 100%, rgba(var(--suggested1)) 0%, rgba(var(--suggested2)) 100%)' },
  { name: '--tabbarGradient', defaultValue: 'linear-gradient(306deg, rgba(var(--suggested2), 0.20) 20.08%, rgba(var(--secondary2), 0.20) 59.19%)' },
  { name: '--iconLightBackgroundPressed', defaultValue: 'radial-gradient(56.25% 56.25% at 50.72% 100%, rgba(var(--secondary2), 0) 0%, rgba(var(--secondary2), 1) 100%), rgba(var(--strokeDefault), 0.49)' },
  { name: '--iconEnableBtn', defaultValue: 'radial-gradient(68.03% 68.03% at 50.72% 91.35%, rgba(var(--secondary)) 0%, rgba(var(--secondary4)) 100%)' },
  { name: '--iconHoverBtn', defaultValue: 'radial-gradient(56.25% 56.25% at 50.72% 100%, rgba(var(--secondary4)) 0%, rgba(var(--secondary2)) 100%)' },
  { name: '--iconPressBtn', defaultValue: 'radial-gradient(56.25% 56.25% at 50.72% 100%, rgba(var(--secondary2)) 0%, rgba(var(--secondary4)) 100%)' },
  { name: '--iconSelectedBtn', defaultValue: 'radial-gradient(56.25% 56.25% at 50.72% 100%, rgba(var(--suggested1), 0) 0%, rgba(var(--primary2)) 100%), rgba(var(--primary), 1)' },
  { name: '--strokeDefaultGradientTop', defaultValue: 'linear-gradient(180deg, rgba(var(--strokeDefault, 1)) 0%, rgba(var(--background), 0) 50%)' },
  { name: '--strokeDefaultGradientBottom', defaultValue: 'linear-gradient(180deg, rgba(var(--background), 0) 0%, rgba(var(--strokeDefault), 1) 50%)' },
  { name: '--gradientBottom', defaultValue: 'linear-gradient(180deg, rgba(var(--background), 1) 21.67%, rgba(var(--background), 0) 99.86%)' },
  { name: '--gradientTop', defaultValue: 'linear-gradient(180deg, rgba(var(--background), 1) 9.19%, rgba(var(--background), 0.) 99.94%)' },
  { name: '--gradientLeft', defaultValue: 'linear-gradient(90deg, rgba(var(--background), 1) 29.33%, rgba(var(--background), 0) 62.07%)' },
  { name: '--gradientSpotlightRow', defaultValue: 'radial-gradient(44.43% 67.08% at 90.54% 24.22%, rgba(var(--secondary2), 0.30) 0%, rgba(var(--secondary), 0) 100%), radial-gradient(88.05% 46.09% at 0% 100%, rgba(var(--secondary), 0.30) 0%, rgba(var(--secondary2), 0) 100%), rgba(var(--secondary), 0.20)' },
  { name: '--pillsAndTagsBgGradient', defaultValue: 'radial-gradient(68.03% 68.03% at 50.72% 91.35%, rgba(var(--secondary), 0.20) 0%, rgba(var(--secondary2), 0.20) 100%), rgba(var(--background), 1)' },
  { name: '--discountButton', defaultValue: 'radial-gradient(77.55% 77.55% at 50.72% 78.7%, rgba(var(--discount), 1) 0%, rgba(26, 205, 65, 0) 100%), rgba(13, 145, 42, 1)' },
  { name: '--discountButtonHover', defaultValue: 'radial-gradient(64.58% 64.58% at 50.72% 75%, rgba(26, 205, 65, 0.00) 0%, rgba(var(--discount), 1) 100%), rgba(13, 145, 42, 1)' },
  { name: '--fieldFilledBg', defaultValue: 'var(--surface)' },
]

const STORAGE_KEY = 'customColorTheme'

export default function ColorPalette() {
  const [colors, setColors] = useState<Record<string, string>>({})
  const [mounted, setMounted] = useState(false)
  const [showVariablesList, setShowVariablesList] = useState(false)

  // Cargar colores al montar
  useEffect(() => {
    loadColorsFromStorage()
    setMounted(true)
  }, [])

  const loadColorsFromStorage = () => {
    const saved = localStorage.getItem(STORAGE_KEY)
    const colorState: Record<string, string> = {}

    if (saved) {
      try {
        const parsedColors = JSON.parse(saved)
        Object.assign(colorState, parsedColors)
      } catch (e) {
        console.error('Error loading colors from storage:', e)
      }
    }

    // Cargar variables por defecto si no están guardadas
    COLOR_VARIABLES.forEach(({ name, defaultValue }) => {
      if (!colorState[name]) {
        colorState[name] = defaultValueToHex(defaultValue)
      }
    })

    setColors(colorState)
    applyColors(colorState)
  }

  const parseRgbParts = (value: string) =>
    value
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0)

  const defaultValueToHex = (value: string): string => {
    const parts = parseRgbParts(value)
    if (parts.length < 3) return '#000000'
    const r = parseInt(parts[0])
    const g = parseInt(parts[1])
    const b = parseInt(parts[2])
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
  }

  const getAlpha = (value: string): string | undefined => {
    const parts = parseRgbParts(value)
    if (parts.length === 4) {
      const alpha = parseFloat(parts[3])
      return Number.isNaN(alpha) ? undefined : alpha.toString()
    }
    return undefined
  }

  const rgbToHex = (rgb: string): string => {
    const parts = parseRgbParts(rgb)
    const r = parseInt(parts[0])
    const g = parseInt(parts[1])
    const b = parseInt(parts[2])
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
  }

  const hexToRgb = (hex: string): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    if (!result) return ''
    const r = parseInt(result[1], 16)
    const g = parseInt(result[2], 16)
    const b = parseInt(result[3], 16)
    return `${r}, ${g}, ${b}`
  }

  const variableHasAlpha = (name: string) => {
    const variable = COLOR_VARIABLES.find((item) => item.name === name)
    return variable ? getAlpha(variable.defaultValue) !== undefined : false
  }

  const applyColors = (colorState: Record<string, string>) => {
    Object.entries(colorState).forEach(([varName, hexColor]) => {
      const rgb = hexToRgb(hexColor)
      if (!rgb) return

      if (variableHasAlpha(varName)) {
        const alpha = getAlpha(COLOR_VARIABLES.find((item) => item.name === varName)?.defaultValue ?? '')
        if (alpha) {
          document.documentElement.style.setProperty(varName, `${rgb}, ${alpha}`)
          return
        }
      }

      document.documentElement.style.setProperty(varName, rgb)
    })
  }

  const handleColorChange = (varName: string, hexColor: string) => {
    const newColors = { ...colors, [varName]: hexColor }
    setColors(newColors)
    applyColors(newColors)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newColors))
  }

  const resetColors = () => {
    const defaultColors: Record<string, string> = {}
    COLOR_VARIABLES.forEach(({ name, defaultValue }) => {
      defaultColors[name] = defaultValueToHex(defaultValue)
    })
    setColors(defaultColors)
    applyColors(defaultColors)
    localStorage.removeItem(STORAGE_KEY)
  }

  const generateCSSVariables = () => {
    let css = ':root {\n'
    
    // Agregar colores editados
    COLOR_VARIABLES.forEach(({ name }) => {
      const value = colors[name]
      if (!value) return

      const rgb = hexToRgb(value)
      if (!rgb) return

      if (variableHasAlpha(name)) {
        const alpha = getAlpha(COLOR_VARIABLES.find((item) => item.name === name)?.defaultValue ?? '')
        if (alpha) {
          css += `  ${name}: ${rgb}, ${alpha};\n`
          return
        }
      }

      css += `  ${name}: ${rgb};\n`
    })

    css += '\n  /* Gradients */\n'
    
    // Agregar gradientes
    GRADIENT_VARIABLES.forEach(({ name, defaultValue }) => {
      css += `  ${name}: ${defaultValue};\n`
    })

    css += '}\n'
    return css
  }

  const copyToClipboard = () => {
    const css = generateCSSVariables()
    navigator.clipboard.writeText(css).then(() => {
      alert('Bloques de variables copiados al portapapeles')
    })
  }

  const downloadVariables = () => {
    const css = generateCSSVariables()
    const blob = new Blob([css], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'color-variables.txt'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target?.result as string
      if (validateAndApplyVariables(content)) {
        alert('Variables aplicadas correctamente desde el archivo')
      } else {
        alert('Error: El archivo no tiene el formato correcto. Asegúrate de que sea un archivo generado por esta herramienta.')
      }
    }
    reader.readAsText(file)
    // Reset input
    event.target.value = ''
  }

  const validateAndApplyVariables = (content: string): boolean => {
    try {
      // Validate basic structure
      if (!content.trim().startsWith(':root {') || !content.trim().endsWith('}')) {
        return false
      }

      // Parse the content and extract color values
      const newColors: Record<string, string> = {}
      let foundVariables = 0

      // Extract color variables (lines like "  --primary: 13, 64, 86;")
      const colorLines = content.split('\n').filter(line =>
        line.trim().startsWith('--') &&
        line.includes(':') &&
        !line.includes('/*') &&
        !line.includes('linear-gradient') &&
        !line.includes('radial-gradient') &&
        !line.includes('rgba(') &&
        !line.includes('var(')
      )

      for (const line of colorLines) {
        const match = line.trim().match(/^--([^:]+):\s*([^;]+);$/)
        if (match) {
          const varName = `--${match[1]}`
          const value = match[2].trim()

          // Validate that this is a defined color variable (not a gradient)
          const isValidVariable = COLOR_VARIABLES.some(v => v.name === varName)
          if (!isValidVariable) {
            // console.log(varName)
            // console.log('Invalid Variable.')
            return false
          }

          // Convert RGB to hex for storage
          const rgbMatch = value.match(/^(\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?$/)
          if (rgbMatch) {
            const r = parseInt(rgbMatch[1])
            const g = parseInt(rgbMatch[2])
            const b = parseInt(rgbMatch[3])
            
            // Validate RGB values are in range
            if (r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) {
              return false
            }

            const hex = `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
            newColors[varName] = hex
            foundVariables++
          } else {
            return false
          }
        }
      }

      // Validate all color variables are present
      if (foundVariables !== COLOR_VARIABLES.length) {
        // console.log(foundVariables)
        // console.log(COLOR_VARIABLES.length)
        return false
      }

      // Apply the colors
      setColors(newColors)
      applyColors(newColors)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newColors))

      return true
    } catch (error) {
      console.error('Error parsing variables file:', error)
      return false
    }
  }

  if (!mounted) return null

  const categories = [...new Set(COLOR_VARIABLES.map(v => v.category))]

  return (
    <div className={styles.colorPaletteWrapper}>
      <div className={styles.topBar}>
        <h3 className={styles.title}>Paleta de Colores</h3>
        <div className={styles.buttons}>
          <button onClick={() => setShowVariablesList(!showVariablesList)} className={styles.showVarsButton}>
            {showVariablesList ? 'Ocultar' : 'Mostrar'} Variables
          </button>
        </div>
      </div>

      {showVariablesList && (
        <div className={styles.variablesSection}>
          <div className={styles.variablesHeader}>
            <h4 className={styles.variablesTitle}>Variables CSS (para copiar y pegar)</h4>
            <div className={styles.variablesActions}>
              <button onClick={copyToClipboard} className={styles.copyButton}>
                📋 Copiar Todo
              </button>
              <button onClick={downloadVariables} className={styles.downloadButton}>
                💾 Descargar .txt
              </button>
              <label className={styles.uploadButton}>
                📁 Subir .txt
                <input
                  type="file"
                  accept=".txt"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>
          <pre className={styles.variablesCode}>
{generateCSSVariables()}
          </pre>
        </div>
      )}

      <div className={styles.colorsContainer}>
        {categories.map(category => (
          <div key={category} className={styles.categorySection}>
            <h4 className={styles.categoryTitle}>{category}</h4>
            <div className={styles.colorGrid}>
              {COLOR_VARIABLES
                .filter(v => v.category === category)
                .map(({ name }) => (
                  <div key={name} className={styles.colorItem}>
                    <label className={styles.colorLabel}>
                      <span className={styles.labelText}>{name.replace('--', '')}</span>
                      <div className={styles.inputWrapper}>
                        <input
                          type="color"
                          value={colors[name] || '#000000'}
                          onChange={(e) => handleColorChange(name, e.target.value)}
                          className={styles.colorInput}
                        />
                        <span className={styles.colorValue}>{colors[name]}</span>
                      </div>
                    </label>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.bottomBar}>
        <button onClick={resetColors} className={styles.resetButton}>
          Restablecer Todos los Colores
        </button>
      </div>
    </div>
  )
}
